import * as React from "react";
import { FvcAnalysisSet, FvcAnalysisSetTest, FvcSession, FvcTest } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { TestQualityGradeIndicator } from "../../Shared/TestQualityGradeIndicator";
import { TestQualityGrade } from "../../Shared/TestQualityGrading";
import { AcceptabilityNextGenState, useAcceptabilityNextGen } from "../../Shared/UseAcceptabilityNextGen";
import { IndividualAcceptabilityStatus } from "../../../misc/api/apiTypes"
import { ErrorBoundary } from "../../../errors/ErrorBoundary";
import { useResultsContext } from "../UseResultsContext";
import { ResultsGroupHeading } from "../../Shared/ResultsGroupHeading";
import { SessionTypes } from "../../../misc/api/ApiSessionTypes";

export interface NextGenTestAcceptabilityProps {
    viewOnly: boolean;
    loading: boolean;
    session: FvcSession;
    analysisSet: FvcAnalysisSet;
    acceptabilityState: AcceptabilityNextGenState;
    onAcceptabilityChange: (testId: string, checked: boolean) => void;
    onFev1AcceptabilityChange?: (testId: string, value: string) => void;
    onFvcAcceptabilityChange?: (testId: string, value: string) => void;
    comparisonAnalysisSet?: FvcAnalysisSet;
}

export interface NextGenTestOverreaderAcceptabilityProps {
    viewOnly: boolean;
    tests: FvcTest[];
    analysisSetTests: FvcAnalysisSetTest[];
    onAcceptabilityChange: (testId: string, checked: boolean) => void;
}

export interface NextGenTestTechnicianAcceptabilityProps {
    tests: FvcTest[];
    comparisonAnalysisSet?: FvcAnalysisSet;
}

export const NextGenTestAcceptability = ({ viewOnly, loading, session, analysisSet, acceptabilityState, onAcceptabilityChange, onFev1AcceptabilityChange, onFvcAcceptabilityChange, comparisonAnalysisSet = undefined }: NextGenTestAcceptabilityProps) => {
    const { fvcSettings } = useCurrentStudyConfigurationContext();
    const { additionalColumnCount } = useResultsContext();

    const acceptabilityOptions = [
        { label: "Undetermined", id: "ART_UNDETERMINED" },
        { label: "Acceptable", id: "ART_ACCEPTABLE" },
        { label: "Usable", id: "ART_USABLE" },
        { label: "Not Acceptable", id: "ART_NOTACCEPTABLE" }
    ]

    return (
        <ErrorBoundary>
            <ResultsGroupHeading resultsCount={session.tests.length} title="Acceptability" />
            <tr>
                <td>Test Acceptability</td>
                {session.tests.sort((a, b) => a.index - b.index).map((t) => {
                    return (
                        <td key={`OverreaderAcceptability${t.id}`}>
                            <input disabled={viewOnly} data-automationid={`test-acceptability-${t.index}`}
                                checked={analysisSet.tests.find(ast => ast.testId === t.id) && analysisSet.tests.find(ast => ast.testId === t.id).accepted !== undefined ? analysisSet.tests.find(ast => ast.testId === t.id).accepted : false}
                                type="checkbox"
                                onChange={(e) => onAcceptabilityChange(t.id, e.target.checked)}
                            />
                        </td>
                    );
                })}
                <td>
                    {fvcSettings.GradingEnabled && session.sessionType === SessionTypes.Fvc ?
                        <>
                            <TestQualityGradeIndicator title="FEV1" loading={loading} grade={acceptabilityState.analysisSet.fev1Grade as TestQualityGrade} />
                            <TestQualityGradeIndicator title="FVC" loading={loading} grade={acceptabilityState.analysisSet.fvcGrade as TestQualityGrade} />
                        </> : null}
                </td>
                {additionalColumnCount - 2 > 0 ? <td colSpan={additionalColumnCount - 2}></td> : null}
            </tr>
            {comparisonAnalysisSet ? <tr>
                <td>Technician Test Acceptability</td>
                {session.tests.sort((a, b) => a.index - b.index).map((t) => {
                    return (
                        <td key={`TechnicianAcceptability${t.id}`}>
                            <input disabled={true} readOnly={true}
                                checked={comparisonAnalysisSet.tests.find(ast => ast.testId === t.id).accepted}
                                data-automationid={`test-acceptability-technician-${t.index}`}
                                type="checkbox"
                            />
                        </td>
                    );
                })}
                {additionalColumnCount - 1 > 0 ? <td colSpan={additionalColumnCount - 1}></td> : null}
            </tr> : null}
            {comparisonAnalysisSet && fvcSettings.IndividualAcceptabilitiesEnabled !== IndividualAcceptabilityStatus.Hidden && session.sessionType === SessionTypes.Fvc ?
                <>
                    {fvcSettings.IndividualAcceptabilitiesEnabled === IndividualAcceptabilityStatus.Overreading ? <tr>
                        <td>FVC Acceptability</td>
                        {session.tests.sort((a, b) => a.index - b.index).map((t) => {
                            return (
                                <td>
                                    <select disabled={viewOnly} className="form-control" value={analysisSet.tests.find(ast => ast.testId === t.id)?.fvcAcceptability} onChange={(e) => onFvcAcceptabilityChange(t.id, e.target.value)}>
                                        {acceptabilityOptions.map(a => (<option id={a.id} value={a.id}>{a.label}</option>))}
                                    </select>
                                </td>
                            );
                        })}
                        {additionalColumnCount - 1 > 0 ? <td colSpan={additionalColumnCount - 1}></td> : null}
                    </tr> : null}
                    <tr>
                        <td>Technician FVC Acceptability</td>
                        {session.tests.sort((a, b) => a.index - b.index).map((t) => {
                            return (
                                <td key={`FVCAcceptability${t.id}`}>
                                    {acceptabilityOptions.find(ao => ao.id === comparisonAnalysisSet.tests.find(ast => ast.testId === t.id)?.fvcAcceptability)?.label}
                                </td>
                            );
                        })}
                        {additionalColumnCount - 1 > 0 ? <td colSpan={additionalColumnCount - 1}></td> : null}
                    </tr>

                    {fvcSettings.IndividualAcceptabilitiesEnabled === IndividualAcceptabilityStatus.Overreading ? <tr>
                        <td>FEV1 Acceptability</td>
                        {session.tests.sort((a, b) => a.index - b.index).map((t) => {
                            return (
                                <td>
                                    <select disabled={viewOnly} className="form-control" value={analysisSet.tests.find(ast => ast.testId === t.id)?.fev1Acceptability} onChange={(e) => onFev1AcceptabilityChange(t.id, e.target.value)}>
                                        {acceptabilityOptions.map(a => (<option id={a.id} value={a.id}>{a.label}</option>))}
                                    </select>
                                </td>
                            );
                        })}
                        {additionalColumnCount - 1 > 0 ? <td colSpan={additionalColumnCount - 1}></td> : null}
                    </tr> : null}
                    <tr>
                        <td>Technician FEV1 Acceptability</td>
                        {session.tests.sort((a, b) => a.index - b.index).map((t) => {
                            return (
                                <td key={`FEV1Acceptability${t.id}`}>
                                    {acceptabilityOptions.find(ao => ao.id === comparisonAnalysisSet.tests.find(ast => ast.testId === t.id)?.fev1Acceptability)?.label}
                                </td>
                            );
                        })}
                        {additionalColumnCount - 1 > 0 ? <td colSpan={additionalColumnCount - 1}></td> : null}
                    </tr>
                </> : null}
        </ErrorBoundary>
    );
}

export const NextGenTestOverreaderAcceptability = (props: NextGenTestOverreaderAcceptabilityProps) => {
    return <>
        {props.tests.sort((a, b) => a.index - b.index).map((t) => {
            return (
                <td key={`OverreaderAcceptability${t.id}`}>
                    <input disabled={props.viewOnly} data-automationid="test-acceptability" data-test-number={t.index}
                        checked={props.analysisSetTests.find(ast => ast.testId === t.id) && props.analysisSetTests.find(ast => ast.testId === t.id).accepted !== undefined ? props.analysisSetTests.find(ast => ast.testId === t.id).accepted : false}
                        type="checkbox"
                        onChange={(e) => props.onAcceptabilityChange(t.id, e.target.checked)}
                    />
                </td>
            );
        })}
    </>;
}

export const NextGenTestTechnicianAcceptability = (props: NextGenTestTechnicianAcceptabilityProps) => {
    return <>
        {props.tests.sort((a, b) => a.index - b.index).map((t) => {
            return (
                <td key={`TechnicianAcceptability${t.id}`}>
                    <input disabled={true} readOnly={true}
                        checked={props.comparisonAnalysisSet.tests.find(ast => ast.testId === t.id).accepted}
                        data-automationid={`test-acceptability-technician-${t.index}`}
                        type="checkbox"
                    />
                </td>
            );
        })}
    </>;
}
